import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import i18n, { uiLocale } from "@/i18n";
import { api, type ApiResponse } from "@/api/client";
import { compareCzech, sortBySearchMatch } from "@ckgo2/shared";
import { formatPrice, isOnRequestBooking } from "@ckgo2/shared/utils";
import { formatDate } from "@/lib/format";
import {
  HOTEL_DEPARTURE_LABELS,
  HOTEL_MEAL_PLAN_LABELS,
  BOOKABLE_LIFECYCLES,
  NON_RUNNING_LIFECYCLES,
  type HotelStaySnapshot,
  type TripDateLifecycle,
} from "@ckgo2/shared/types";
import { enumLabel } from "@/i18n/enumLabels";
import { ArrowLeft, Check } from "lucide-react";
import { TripStep, CustomerStep, DetailsStep, ReviewStep } from "@/components/orders/new-order";
import type {
  Trip,
  TripDate,
  Customer,
  Participant,
  NewCustomerForm,
  Step,
} from "@/components/orders/new-order";

const STEPS: { id: Step; label: string }[] = [
  { id: "trip", label: i18n.t("orders.new.steps.trip") },
  { id: "customer", label: i18n.t("orders.new.steps.customer") },
  { id: "details", label: i18n.t("orders.new.steps.details") },
  { id: "review", label: i18n.t("orders.new.steps.review") },
];

const STEP_IDS = STEPS.map((s) => s.id);

/**
 * Termín z `GET /api/admin/trips/:id` nese i `lifecycle` a `isPrivate` (admin vidí i soukromé
 * a draft termíny); typ kroku `TripStep` je nezná.
 */
type TripDetailDate = TripDate & { lifecycle: TripDateLifecycle; isPrivate: boolean };

const NON_RUNNING: readonly TripDateLifecycle[] = NON_RUNNING_LIFECYCLES;
const BOOKABLE: readonly TripDateLifecycle[] = BOOKABLE_LIFECYCLES;

// Minimal lead shape needed to prefill the wizard when converting (?leadId=).
interface LeadPrefill {
  id: string;
  tripId?: string | null;
  /** Vypsaný odjezd, který zákazník v poptávce vybral (řez 01, F1.6); průvodce ho předvybere. */
  tripDateId?: string | null;
  customerId?: string | null;
  /** Inquiry profile — only the hotel snapshot is used here, as a read-only reminder. */
  profile?: { hotel?: HotelStaySnapshot } | null;
  /** Currency the lead (and therefore its hotel surcharges) is denominated in. */
  currency?: string | null;
  // Contact fields — used to pre-select/pre-fill the customer when the lead has no customerId
  // yet (a web inquiry resolves one, but a manually-created lead often won't).
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  paxAdults: number;
  paxChildren: number;
  requestedDate?: string | null;
  customerNotes?: string | null;
  internalNotes?: string | null;
}

/** "Deluxe (+3 890 Kč)" / "Standard (v ceně)" — flat surcharges for the whole stay. The currency
 *  is the converted LEAD's (the snapshot is its data); undefined falls back to the app default,
 *  same as every other price in this wizard. */
function hotelSurcharge(
  name: string,
  surcharge: number,
  includedLabel: string,
  currency?: string,
): string {
  return surcharge > 0
    ? `${name} (+${formatPrice(surcharge, currency, uiLocale())})`
    : `${name} (${includedLabel})`;
}

function HotelRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-right font-medium text-gray-900 dark:text-white">{value || "—"}</dd>
    </div>
  );
}

export default function NewOrderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get("leadId");

  // Form state
  const [step, setStep] = useState<Step>("trip");
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [selectedDate, setSelectedDate] = useState<TripDate | null>(null);
  const [requestedDate, setRequestedDate] = useState<string>("");
  const [customerMode, setCustomerMode] = useState<"search" | "new">("search");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [tripSearch, setTripSearch] = useState("");

  // New customer form
  const [newCustomer, setNewCustomer] = useState<NewCustomerForm>({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    street: "",
    city: "",
    zip: "",
  });

  // Order details
  const [adultsCount, setAdultsCount] = useState(1);
  const [childrenCount, setChildrenCount] = useState(0);
  const [participants, setParticipants] = useState<Participant[]>([{ name: "" }]);
  const [customerNotes, setCustomerNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [status, setStatus] = useState("confirmed");

  // Fetch trips
  const { data: tripsData, isLoading: tripsLoading } = useQuery({
    queryKey: ["trips-for-order"],
    queryFn: () => api.get<ApiResponse<Trip[]>>("/api/admin/trips"),
  });

  // Fetch selected trip details (includes dates)
  const { data: tripDetailsData, isLoading: tripDetailsLoading } = useQuery({
    queryKey: ["trip-details", selectedTrip?.id],
    queryFn: () =>
      api.get<ApiResponse<Trip & { dates: TripDetailDate[] }>>(
        `/api/admin/trips/${selectedTrip?.id}`,
      ),
    enabled: !!selectedTrip?.id,
  });

  // Fetch customers for the picker (the list is now paginated — pull a generous page).
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ["customers-for-order"],
    queryFn: () => api.get<ApiResponse<Customer[]>>("/api/admin/customers?limit=100"),
  });

  // Lead being converted (optional). Prefills trip/customer/pax/notes once lists load.
  const { data: leadData } = useQuery({
    queryKey: ["lead-for-order", leadId],
    queryFn: () => api.get<ApiResponse<LeadPrefill>>(`/api/admin/leads/${leadId}`),
    enabled: !!leadId,
  });

  const trips = tripsData?.data || [];
  const customers = customersData?.data || [];
  const tripDates = tripDetailsData?.data?.dates || [];
  const hotelStay = leadData?.data?.profile?.hotel ?? null;
  const hotelCurrency = leadData?.data?.currency ?? undefined;

  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    const lead = leadData?.data;
    // Čeká na DOKONČENÍ seznamů zájezdů a zákazníků, ne na jejich data: bez čekání se efekt
    // spustil nad prázdným seznamem, `prefilled` se nastavil a zájezd se už nepředvybral.
    // Po chybě dotazu se předvyplní, co jde (zákazník pak z kontaktu poptávky).
    if (!lead || prefilled || tripsLoading || customersLoading) return;
    if (lead.tripId) {
      const t = trips.find((x) => x.id === lead.tripId);
      if (t) setSelectedTrip(t);
    }
    // Pre-select the customer: by the lead's customerId, else match an existing one by the
    // contact email, else drop into "new customer" mode pre-filled from the lead's contact —
    // so converting a lead never makes the agent re-type who the customer is.
    const byId = lead.customerId ? customers.find((x) => x.id === lead.customerId) : undefined;
    const byEmail =
      !byId && lead.contactEmail
        ? customers.find((x) => x.email.toLowerCase() === lead.contactEmail!.toLowerCase())
        : undefined;
    const matched = byId ?? byEmail;
    if (matched) {
      setSelectedCustomer(matched);
    } else if (lead.contactEmail || lead.contactName) {
      const [firstName, ...rest] = (lead.contactName ?? "").trim().split(/\s+/);
      setCustomerMode("new");
      setNewCustomer((prev) => ({
        ...prev,
        email: lead.contactEmail ?? "",
        firstName: firstName ?? "",
        lastName: rest.join(" "),
        phone: lead.contactPhone ?? "",
      }));
    }
    setAdultsCount(lead.paxAdults || 1);
    setChildrenCount(lead.paxChildren || 0);
    if (lead.requestedDate) setRequestedDate(lead.requestedDate);
    if (lead.customerNotes) setCustomerNotes(lead.customerNotes);
    if (lead.internalNotes) setInternalNotes(lead.internalNotes);
    setStatus("inquiry"); // converted leads start as a fresh inquiry-stage order
    setPrefilled(true);
  }, [leadData, trips, customers, prefilled, tripsLoading, customersLoading]);

  // Odjezd poptávky, dokud je vybraný její zájezd (termíny patří `selectedTrip`, klíč dotazu).
  // Po přepnutí na jiný zájezd je `undefined`, takže varování níž nezůstane viset.
  const leadTripDateId = leadData?.data?.tripDateId;
  const leadDeparture =
    leadTripDateId && selectedTrip?.id === leadData?.data?.tripId
      ? tripDetailsData?.data?.dates.find((d) => d.id === leadTripDateId)
      : undefined;
  const leadDepartureNotRunning =
    leadDeparture !== undefined && NON_RUNNING.includes(leadDeparture.lifecycle);
  // Týž predikát jako automatický převod (`leadDepartureForOrder`): předvybrat jen veřejný
  // odjezd v prodeji — ne `draft` ani cizí soukromý termín, který z odjezdu mezitím vznikl.
  const leadDepartureBookable =
    leadDeparture !== undefined &&
    !leadDeparture.isPrivate &&
    BOOKABLE.includes(leadDeparture.lifecycle);

  // Termíny zájezdu dorazí až po jeho předvybrání, proto vlastní jednorázový efekt.
  // `departurePrefilled` brání tomu, aby refetch termínů přepsal ruční volbu obchodníka.
  // Odjezd mimo prodej se nepředvybírá, obchodník vybere jiný (u neběžícího varování
  // v kroku Zájezd).
  const [departurePrefilled, setDeparturePrefilled] = useState(false);
  useEffect(() => {
    if (departurePrefilled || !leadDeparture) return;
    if (leadDepartureBookable) setSelectedDate(leadDeparture);
    setDeparturePrefilled(true);
  }, [leadDeparture, leadDepartureBookable, departurePrefilled]);

  // Hledání v obou výběrech filtruje i řadí podle shody s nadpisem řádku (název zájezdu,
  // celé jméno zákazníka), při stejné shodě (i bez hledání) abecedně týmž nadpisem
  // (`compareCzech`). Krok zákazníka ukazuje jen prvních 10, proto musí „Jan" dát Jana dopředu.
  const filteredTrips = sortBySearchMatch(trips, tripSearch, {
    name: (trip) => trip.title,
    other: (trip) => [trip.slug],
    tieBreak: (a, b) => compareCzech(a.title, b.title),
  });

  const customerName = (c: (typeof customers)[number]) =>
    `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim();
  const filteredCustomers = sortBySearchMatch(customers, customerSearch, {
    name: customerName,
    other: (c) => [c.email, c.phone],
    tieBreak: (a, b) => compareCzech(customerName(a), customerName(b)),
  });

  // Update participants array when adults count changes
  useEffect(() => {
    const totalCount = adultsCount + childrenCount;
    setParticipants((prev) => {
      if (prev.length < totalCount) {
        return [...prev, ...Array(totalCount - prev.length).fill({ name: "" })];
      } else if (prev.length > totalCount) {
        return prev.slice(0, totalCount);
      }
      return prev;
    });
  }, [adultsCount, childrenCount]);

  // Calculate total price (on-request uses trip.priceFrom; fixed-departures uses tripDate.price)
  const isOnRequest = isOnRequestBooking(selectedTrip ?? {});
  const pricePerPerson = isOnRequest
    ? selectedTrip?.priceFrom || 0
    : selectedDate
      ? selectedDate.price
      : 0;
  const totalPrice = pricePerPerson * (adultsCount + childrenCount * 0.5);
  const depositAmount = Math.round(totalPrice * 0.5);

  // Create customer mutation
  const createCustomerMutation = useMutation({
    mutationFn: (data: NewCustomerForm) =>
      api.post<ApiResponse<Customer>>("/api/admin/customers", data),
    onSuccess: (data) => {
      setSelectedCustomer(data.data);
      setCustomerMode("search");
      queryClient.invalidateQueries({ queryKey: ["customers-for-order"] });
    },
  });

  // Create order mutation. When a leadId is passed, the backend createOrder pipeline links the
  // lead back (convertedOrderId + sold) idempotently — no separate PUT needed here anymore.
  const createOrderMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<ApiResponse<{ id: string }>>("/api/admin/orders", data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      if (leadId) queryClient.removeQueries({ queryKey: ["leads"] });
      navigate(`/orders/${data.data.id}`);
    },
  });

  const handleCreateCustomer = async () => {
    if (!newCustomer.email || !newCustomer.firstName || !newCustomer.lastName) {
      return;
    }
    createCustomerMutation.mutate(newCustomer);
  };

  const handleSubmit = async () => {
    if (!selectedTrip || !selectedCustomer) return;
    if (isOnRequest ? !requestedDate : !selectedDate) return;

    const orderData = {
      customerId: selectedCustomer.id,
      tripId: selectedTrip.id,
      ...(leadId ? { leadId } : {}),
      ...(isOnRequest ? { requestedDate } : { tripDateId: selectedDate!.id }),
      status,
      participants: participants.filter((p) => p.name),
      adultsCount,
      childrenCount,
      totalPrice: Math.round(totalPrice),
      customerNotes,
      internalNotes,
      source: "phone",
    };

    createOrderMutation.mutate(orderData);
  };

  const canProceedToCustomer =
    !!selectedTrip && (isOnRequest ? !!requestedDate : !!selectedDate) && !tripDetailsLoading;
  const canProceedToDetails = !!selectedCustomer;
  const canProceedToReview = participants.some((p) => p.name);

  return (
    <div className="mx-auto max-w-4xl">
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <button onClick={() => navigate("/orders")} className="btn-ghost btn-sm">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("orders.new.title")}
          </h1>
          <p className="text-sm text-gray-500">{t("orders.new.subtitle")}</p>
        </div>
      </div>

      {/* Progress steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {STEPS.map((s, index) => (
            <div key={s.id} className="flex items-center">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                  step === s.id
                    ? "bg-primary-600 text-white"
                    : index < STEP_IDS.indexOf(step)
                      ? "bg-success-500 text-white"
                      : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                }`}
              >
                {index < STEP_IDS.indexOf(step) ? <Check className="h-4 w-4" /> : index + 1}
              </div>
              <span
                className={`ml-2 hidden text-sm sm:block ${
                  step === s.id ? "font-medium text-gray-900 dark:text-white" : "text-gray-500"
                }`}
              >
                {s.label}
              </span>
              {index < 3 && <div className="mx-4 h-0.5 w-8 bg-gray-200 sm:w-16 dark:bg-gray-700" />}
            </div>
          ))}
        </div>
      </div>

      {/* What the customer actually asked for (vize D4). READ-ONLY on purpose: it is the
          inquiry snapshot, not an editable field — the specialist composes the offer from it,
          and the stay's end date reaches the order via the API (createOrder reads the lead). */}
      {hotelStay && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
          <h2 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
            {t("orders.new.hotel.title")}
          </h2>
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <HotelRow
              label={t("orders.new.hotel.stay")}
              value={`${formatDate(hotelStay.stayFrom)} – ${formatDate(hotelStay.stayTo)} (${t(
                "orders.new.hotel.nights",
                { count: hotelStay.nights },
              )})`}
            />
            <HotelRow
              label={t("orders.new.hotel.room")}
              value={
                hotelStay.room
                  ? hotelSurcharge(
                      hotelStay.room.name,
                      hotelStay.room.surcharge,
                      t("orders.new.hotel.included"),
                      hotelCurrency,
                    )
                  : null
              }
            />
            <HotelRow
              label={t("orders.new.hotel.meal")}
              value={
                hotelStay.mealPlan
                  ? hotelSurcharge(
                      enumLabel(HOTEL_MEAL_PLAN_LABELS, hotelStay.mealPlan.plan),
                      hotelStay.mealPlan.surcharge,
                      t("orders.new.hotel.included"),
                      hotelCurrency,
                    )
                  : null
              }
            />
            <HotelRow
              label={t("orders.new.hotel.departure")}
              value={
                hotelStay.departure ? enumLabel(HOTEL_DEPARTURE_LABELS, hotelStay.departure) : null
              }
            />
          </dl>
        </div>
      )}

      {step === "trip" && leadDeparture && leadDepartureNotRunning && (
        <div className="text-warning-700 dark:text-warning-400 mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm dark:border-gray-700 dark:bg-gray-800/50">
          {t("orders.new.leadDepartureNotRunning", { date: formatDate(leadDeparture.startDate) })}
        </div>
      )}

      {/* Step content */}
      <div className="card">
        {step === "trip" && (
          <TripStep
            tripSearch={tripSearch}
            setTripSearch={setTripSearch}
            tripsLoading={tripsLoading}
            filteredTrips={filteredTrips}
            selectedTrip={selectedTrip}
            setSelectedTrip={setSelectedTrip}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            requestedDate={requestedDate}
            setRequestedDate={setRequestedDate}
            tripDetailsLoading={tripDetailsLoading}
            tripDates={tripDates}
            canProceedToCustomer={canProceedToCustomer}
            onNext={() => setStep("customer")}
          />
        )}

        {step === "customer" && (
          <CustomerStep
            customerMode={customerMode}
            setCustomerMode={setCustomerMode}
            customerSearch={customerSearch}
            setCustomerSearch={setCustomerSearch}
            customersLoading={customersLoading}
            filteredCustomers={filteredCustomers}
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
            newCustomer={newCustomer}
            setNewCustomer={setNewCustomer}
            handleCreateCustomer={handleCreateCustomer}
            createCustomerPending={createCustomerMutation.isPending}
            canProceedToDetails={canProceedToDetails}
            onBack={() => setStep("trip")}
            onNext={() => setStep("details")}
          />
        )}

        {step === "details" && (
          <DetailsStep
            adultsCount={adultsCount}
            setAdultsCount={setAdultsCount}
            childrenCount={childrenCount}
            setChildrenCount={setChildrenCount}
            participants={participants}
            setParticipants={setParticipants}
            status={status}
            setStatus={setStatus}
            customerNotes={customerNotes}
            setCustomerNotes={setCustomerNotes}
            internalNotes={internalNotes}
            setInternalNotes={setInternalNotes}
            canProceedToReview={canProceedToReview}
            onBack={() => setStep("customer")}
            onNext={() => setStep("review")}
          />
        )}

        {step === "review" && (
          <ReviewStep
            selectedTrip={selectedTrip}
            selectedDate={selectedDate}
            requestedDate={requestedDate}
            selectedCustomer={selectedCustomer}
            adultsCount={adultsCount}
            childrenCount={childrenCount}
            participants={participants}
            pricePerPerson={pricePerPerson}
            totalPrice={totalPrice}
            depositAmount={depositAmount}
            customerNotes={customerNotes}
            internalNotes={internalNotes}
            handleSubmit={handleSubmit}
            createOrderPending={createOrderMutation.isPending}
            createOrderError={createOrderMutation.isError}
            onBack={() => setStep("details")}
          />
        )}
      </div>
    </div>
  );
}
